import axios from "axios";
import { FileWithId, UploadFileData } from "../models/uploaded-file";
import { axiosService } from "./axios.service";
import { auth } from "../config/firebase";

const BASE_URL = import.meta.env.VITE_BACKEND_URL;

export enum FileTypes {
  PDF = "pdf",
  IMAGE_JPEG = "jpeg",
  IMAGE_GIF = "gif",
  IMAGE_WEBP = "webp",
  IMAGE_BMP = "bmp",
  IMAGE_JPG = "jpg",
  IMAGE_PNG = "png",
  DOCUMENT = "txt",
  ZIP = "zip",
}

export enum FileShortType {
  DOCUMENT = "document",
  IMAGE = "image",
  PDF = "pdf",
  ZIP = "zip",
  ELSE = "else",
}

export class FileService {
  static openFileOnBrowser(fileWithId: FileWithId | null): void {
    if (!fileWithId) return;
    const content = fileWithId.content as File;
    if (!content) {
      console.error("File not found!");
      return;
    }
    const fileReader = new FileReader();
    fileReader.onload = function (e) {
      const fileContent = e.target?.result;
      if (fileContent) {
        const blob = new Blob([content], { type: content.type });
        const url = URL.createObjectURL(blob);
        window.open(url);
      }
    };
    fileReader.readAsDataURL(content);
  }
  static async deleteFile(fileId: string) {
    try {
      await axiosService.delete(`/user/delete-attachment/${fileId}`);
    } catch (error) {
      console.error(error);
    }
  }

  static async deleteFiles(fileIds: string) {
    try {
      await axiosService.delete(`/user/delete-attachments/${fileIds}`);
    } catch (error) {
      console.error(error);
    }
  }
  static async uploadFiles(
    filesWithId: FileWithId[]
  ): Promise<UploadFileData[] | undefined> {
    try {
      const formData = new FormData();
      for (let i = 0; i < filesWithId.length; i++) {
        const file = filesWithId[i].content as File;
        if (file) {
          formData.append(file.name ?? `${i}`, file);
        }
      }
      const response: UploadFileData[] = await axiosService.patch(
        "/user/attachments",
        formData,
        true
      );

      return response;
    } catch (error) {
      console.error(error);
    }
  }

  static async handleCrmFileAction(
    file: FileWithId,
    action: "download" | "preview"
  ) {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return;
      const response = await axios.get(
        `${BASE_URL}/user/downloadattachment/${file.id}`,
        {
          responseType: "blob",
          headers: {
            Authorization: token,
          },
        }
      );

      const url = window.URL.createObjectURL(response.data);

      if (action === "preview") {
        window.open(url, "_blank");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = file.content?.name || "downloaded-file";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error handling file action:", error);
    }
  }

  // Convert file to base64
  static convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;

      reader.readAsDataURL(file);
    });
  }

  // Convert bytes to human-readable format
  static convertBytesToHumanReadable(bytes: number): string {
    if (bytes < 1024) return `${bytes} Bytes`;
    else if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    else if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    else return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  static getFileTypeByName(fileName: string): FileTypes {
    const splittedName = fileName.split(".");
    return splittedName[splittedName.length - 1] as FileTypes;
  }

  // Get short name of file type
  static getFileTypeShortName(fileType: string): string | undefined {
    switch (fileType) {
      case FileTypes.DOCUMENT:
        return FileShortType.DOCUMENT;
      case FileTypes.IMAGE_JPEG:
      case FileTypes.IMAGE_GIF:
      case FileTypes.IMAGE_BMP:
      case FileTypes.IMAGE_JPG:
      case FileTypes.IMAGE_PNG:
      case FileTypes.IMAGE_WEBP:
        return FileShortType.IMAGE;
      case FileTypes.PDF:
        return FileShortType.PDF;
      case FileTypes.ZIP:
        return FileShortType.ZIP;
      default:
        return FileShortType.ELSE;
    }
  }

  // Download a single or multiple files
  static async downloadFiles(files: File | File[]): Promise<void> {
    const fileArray = Array.isArray(files) ? files : [files];

    if (fileArray.length === 1) {
      // Single file download
      const file = fileArray[0];
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    // Multiple files zip download
    const zip = new Blob(
      fileArray.map((file) => new Blob([file], { type: file.type })),
      { type: "application/zip" }
    );

    const url = URL.createObjectURL(zip);
    const link = document.createElement("a");
    link.href = url;
    link.download = "downloaded_files.zip";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
